/**
 * The library as a shelf of packs.
 *
 * A flat list of three hundred sounds is a search box, not a browser. Packs are how a person
 * actually reaches for sound: a kit for the genre they are working in, the 808s when they want
 * a bass line, the loops when they want something to write over. Nothing here changes what is
 * rendered — a pack is a *view* over `LIBRARY_CATALOGUE`, keyed by identifier — but the order
 * of this list is also the order a first run fills the library in, so the packs a board is
 * unplayable without are the ones that arrive first.
 *
 * Packs overlap on purpose. The 808 Sub Kick belongs in the core kit *and* in Trap Essentials,
 * and installing both does not render it twice: the installer keys on the sound's file name,
 * so the second pack finds it already there.
 */

import { LIBRARY_CATALOGUE, libraryEntriesInFamily, libraryEntriesWithIDs } from './catalogue';
import type { LibraryEntry } from './entry';

export interface LibraryPack {
  id: string;
  name: string;
  /** Small uppercase label above the name on the card. */
  kicker: string;
  summary: string;
  symbolName: string;
  /** The sounds this pack installs, in the order they are rendered. */
  entries: readonly LibraryEntry[];
}

/**
 * The kit that has to exist before the app is worth opening: one of each thing a beat needs.
 *
 * Sixteen sounds is a full 4 × 4 board, and drums are the cheapest thing in the library to
 * render — every one of these is under half a second long — so a first run can finish this
 * batch and let the user play while the other two hundred and eighty fill in behind them.
 */
export const INSTANT_KIT_IDS: readonly string[] = [
  'kick-808-sub',
  'kick-house',
  'kick-acoustic',
  'kick-punch',
  'snare-trap',
  'snare-acoustic',
  'snare-fat',
  'snare-tight',
  'hat-closed-tight',
  'hat-closed-loose',
  'hat-open-short',
  'clap-single',
  'clap-stacked',
  'perc-rim',
  'perc-tom-low',
  'perc-crash',
];

export function instantKitEntries(): LibraryEntry[] {
  return libraryEntriesWithIDs(INSTANT_KIT_IDS);
}

/** Curated cross-family selections. Every identifier is checked against the catalogue at
 * build time by the library's own count check, so a typo is a missing sound, not a crash. */
const TRAP_IDS: readonly string[] = [
  'kick-808-sub',
  'kick-trap',
  'kick-knock',
  'snare-trap',
  'snare-snap',
  'snare-clap-layer',
  'hat-closed-tight',
  'hat-closed-bright',
  'hat-pitched-high',
  'hat-pitched-low',
  'hat-open-short',
  'clap-tight',
  'clap-stacked',
  '808-c1',
  '808-ds1',
  '808-f1',
  '808-g1',
  '808-as1',
  'fx-riser-short',
  'fx-sub-drop',
  'fx-impact-deep',
  'loop-trap-140',
];

const LOFI_IDS: readonly string[] = [
  'kick-lofi',
  'kick-boombap',
  'kick-soft',
  'snare-lofi',
  'snare-brush',
  'snare-rim',
  'snare-deep',
  'hat-closed-dark',
  'hat-closed-loose',
  'hat-pedal',
  'hat-shaker',
  'clap-lofi',
  'perc-rim',
  'perc-woodblock',
  'perc-conga-low',
  'fx-vinyl-crackle',
  'fx-tape-stop',
  'loop-lofi-80',
  'loop-lofi-90',
  'loop-boombap-90',
];

const HOUSE_IDS: readonly string[] = [
  'kick-house',
  'kick-techno',
  'kick-tight',
  'kick-distorted',
  'snare-tight',
  'snare-wide',
  'snare-piccolo',
  'hat-closed-tight',
  'hat-closed-bright',
  'hat-open-short',
  'hat-open-long',
  'hat-shaker',
  'clap-single',
  'clap-wide',
  'clap-reverb',
  'perc-cowbell',
  'perc-clave',
  'perc-tambourine',
  'perc-crash',
  'fx-sweep-up',
  'fx-sweep-down',
  'loop-house-120',
];

/**
 * The shelf, in install order.
 *
 * Drums, then the low end, then loops and FX, then the melodic material last: the melodic set
 * is more than half the library by both count and bytes, and a phone that gets interrupted
 * halfway through a first run should still have a complete kit rather than half an octave of
 * bells.
 */
export const LIBRARY_PACKS: readonly LibraryPack[] = [
  {
    id: 'core-drums',
    name: 'Core Drum Kit',
    kicker: 'Every drum',
    summary: 'Fifty-eight one-shots: kicks, snares, hats, claps and percussion across every style.',
    symbolName: 'circle.circle.fill',
    entries: libraryEntriesInFamily('drums'),
  },
  {
    id: 'bass-808',
    name: '808 & Bass',
    kicker: 'Play a bass line',
    summary: 'Every semitone from C1 to C3 as an 808, plus six bass timbres at three pitches.',
    symbolName: 'waveform.path',
    entries: libraryEntriesInFamily('bass'),
  },
  {
    id: 'trap',
    name: 'Trap Essentials',
    kicker: 'Half-time',
    summary: 'Hard kicks, snapping snares, pitched hats and the 808s a trap line is built from.',
    symbolName: 'flame.fill',
    entries: libraryEntriesWithIDs(TRAP_IDS),
  },
  {
    id: 'lofi',
    name: 'Lo-Fi Kit',
    kicker: 'Dusty',
    summary: 'Crushed and filtered drums, brushes and rim clicks, with a vinyl bed and a tape stop.',
    symbolName: 'dial.medium.fill',
    entries: libraryEntriesWithIDs(LOFI_IDS),
  },
  {
    id: 'house',
    name: 'House Kit',
    kicker: 'Four to the floor',
    summary: 'Punchy kicks, wide claps, open hats and the percussion that keeps a groove moving.',
    symbolName: 'square.grid.3x3.fill',
    entries: libraryEntriesWithIDs(HOUSE_IDS),
  },
  {
    id: 'loops',
    name: 'Drum Loops',
    kicker: 'Write over it',
    summary: 'Eight full drum loops at 80 to 140 BPM, seamless and tempo-tagged.',
    symbolName: 'arrow.trianglehead.2.clockwise',
    entries: libraryEntriesInFamily('loops'),
  },
  {
    id: 'fx',
    name: 'FX & Transitions',
    kicker: 'Between sections',
    summary: 'Risers, impacts, sweeps, a tape stop, a glitch burst and three synthetic vocal chops.',
    symbolName: 'aqi.medium',
    entries: libraryEntriesInFamily('fx'),
  },
  {
    id: 'chords',
    name: 'Chord Stabs',
    kicker: 'Instant progression',
    summary: 'Five chord qualities in C, F and G. Three pads already make a progression.',
    symbolName: 'music.note.list',
    entries: libraryEntriesInFamily('chords'),
  },
  {
    id: 'melodic',
    name: 'Melodic One-Shots',
    kicker: 'Play the grid',
    summary: 'Twelve instruments across a full chromatic octave: keys, pads, bells, leads.',
    symbolName: 'chart.bar.fill',
    entries: libraryEntriesInFamily('melodic'),
  },
];

export function libraryPack(id: string): LibraryPack | undefined {
  return LIBRARY_PACKS.find((pack) => pack.id === id);
}

/**
 * Every sound in pack order, with duplicates dropped.
 *
 * This is the order a full first run renders in. It is not the same as `LIBRARY_CATALOGUE`'s
 * order: a sound that three packs share is rendered when the earliest of them comes up, so the
 * user has a usable Trap kit before the melodic set has started.
 */
export function libraryInstallOrder(): LibraryEntry[] {
  const seen = new Set<string>();
  const ordered: LibraryEntry[] = [];
  for (const id of INSTANT_KIT_IDS) seen.add(id);
  for (const entry of instantKitEntries()) ordered.push(entry);
  for (const pack of LIBRARY_PACKS) {
    for (const entry of pack.entries) {
      if (seen.has(entry.id)) continue;
      seen.add(entry.id);
      ordered.push(entry);
    }
  }
  // Anything no pack claims still ships — a sound that exists but cannot be reached is a
  // rendering cost with no user.
  for (const entry of LIBRARY_CATALOGUE) {
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    ordered.push(entry);
  }
  return ordered;
}
